/** Llamadas HTTP del feature Atenciones (Manual SISS Atención de Usuarios). */
import { apiDownload, apiRequest } from "../../lib/api";

export type TipoAtencion = "Solicitud" | "Consulta" | "Emergencia" | "Reclamo";
export type CanalRecepcion =
  | "presencial"
  | "telefono"
  | "email"
  | "whatsapp"
  | "carta"
  | "libro";
export type EstadoAtencion = "EnProceso" | "Resuelto" | "Denegado";

export interface Atencion {
  id: number;
  empresa_id: number;
  anio: number;
  numero_atencion: number;
  fecha_ingreso: string;
  canal_recepcion: CanalRecepcion;
  tipo: TipoAtencion;
  usuario_nombre: string;
  usuario_rut: string | null;
  usuario_telefono: string | null;
  usuario_email: string | null;
  socio_id: number | null;
  contrato_id: number | null;
  descripcion: string;
  fecha_limite_respuesta: string | null;
  estado: EstadoAtencion;
  tipo_solucion: string | null;
  fecha_respuesta: string | null;
  created_by_id: number | null;
  respondido_por_id: number | null;
  observaciones: string | null;
  created_at: string;
  updated_at: string;
  /** OT vigente (no cancelada) a la que se derivó, si hay. */
  ot_id: number | null;
  ot_numero: string | null;
}

export interface AtencionListResponse {
  items: Atencion[];
  total: number;
  limit: number;
  offset: number;
}

export interface AtencionFilters {
  tipo?: TipoAtencion | null;
  estado?: EstadoAtencion | null;
  canal?: CanalRecepcion | null;
  desde?: string | null;
  hasta?: string | null;
  q?: string | null;
  /** Sólo en proceso con plazo SISS vencido (alerta del Inicio). */
  fuera_de_plazo?: boolean | null;
  /** En proceso con plazo en los próximos 3 días. */
  por_vencer?: boolean | null;
  limit?: number;
  offset?: number;
}

export interface AtencionCreateInput {
  tipo: TipoAtencion;
  canal_recepcion: CanalRecepcion;
  usuario_nombre: string;
  descripcion: string;
  usuario_rut?: string | null;
  usuario_telefono?: string | null;
  usuario_email?: string | null;
  socio_id?: number | null;
  contrato_id?: number | null;
  fecha_ingreso?: string | null;
  observaciones?: string | null;
}

export interface AtencionUpdateInput {
  tipo?: TipoAtencion;
  canal_recepcion?: CanalRecepcion;
  usuario_nombre?: string;
  usuario_rut?: string | null;
  usuario_telefono?: string | null;
  usuario_email?: string | null;
  socio_id?: number | null;
  contrato_id?: number | null;
  descripcion?: string;
  observaciones?: string | null;
}

export interface ResponderInput {
  estado: "Resuelto" | "Denegado";
  tipo_solucion: string;
  fecha_respuesta?: string | null;
}

function toQuery(filters: AtencionFilters): string {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(filters)) {
    if (v === null || v === undefined || v === "") continue;
    params.set(k, String(v));
  }
  return params.toString();
}

export function fetchAtenciones(
  filters: AtencionFilters = {},
): Promise<AtencionListResponse> {
  const qs = toQuery(filters);
  return apiRequest<AtencionListResponse>(
    `/api/v1/atenciones${qs ? `?${qs}` : ""}`,
  );
}

export function fetchAtencion(id: number): Promise<Atencion> {
  return apiRequest<Atencion>(`/api/v1/atenciones/${id}`);
}

export function crearAtencion(payload: AtencionCreateInput): Promise<Atencion> {
  return apiRequest<Atencion>("/api/v1/atenciones", {
    method: "POST",
    body: payload,
  });
}

export function actualizarAtencion(
  id: number,
  payload: AtencionUpdateInput,
): Promise<Atencion> {
  return apiRequest<Atencion>(`/api/v1/atenciones/${id}`, {
    method: "PATCH",
    body: payload,
  });
}

export function responderAtencion(
  id: number,
  payload: ResponderInput,
): Promise<Atencion> {
  return apiRequest<Atencion>(`/api/v1/atenciones/${id}/responder`, {
    method: "POST",
    body: payload,
  });
}

export function descargarCsv(filters: AtencionFilters = {}): Promise<void> {
  const qs = toQuery(filters);
  return apiDownload(
    `/api/v1/atenciones/export.csv${qs ? `?${qs}` : ""}`,
    "atenciones.csv",
  );
}

/** Comprobante en PDF (una hoja, con los datos del comité) que arma el servidor. */
export function descargarComprobante(a: Atencion): Promise<void> {
  return apiDownload(
    `/api/v1/atenciones/${a.id}/comprobante.pdf`,
    `comprobante_atencion_${folioAtencion(a)}.pdf`,
  );
}

export function folioAtencion(a: Atencion): string {
  return `${a.anio}-${String(a.numero_atencion).padStart(5, "0")}`;
}

export const TIPO_LABELS: Record<TipoAtencion, string> = {
  Solicitud: "Solicitud",
  Consulta: "Consulta",
  Emergencia: "Emergencia",
  Reclamo: "Reclamo",
};

export const CANAL_LABELS: Record<CanalRecepcion, string> = {
  presencial: "Presencial",
  telefono: "Teléfono",
  email: "Correo",
  whatsapp: "WhatsApp",
  carta: "Carta",
  libro: "Libro de reclamos",
};

export const ESTADO_LABELS: Record<EstadoAtencion, string> = {
  EnProceso: "En proceso",
  Resuelto: "Resuelto",
  Denegado: "Denegado",
};
